import React, { useEffect, useState } from 'react';
import { DatePicker, Input, Modal } from '@roo/roo';
import Hls from 'hls.js';
import utils from '@/utils/utils';
import consts from './consts';
import url from '@/utils/url';
import request from '@/utils/request';

export interface PlaybackLiveData {
    id: number;
    type: number;
    title: string;
}

interface IProps {
    live: PlaybackLiveData;
    updateLiveState: (
        liveId: number,
        targetState: number,
        playBackStartTime?: number,
        playBackEndTime?: number,
        title?: string,
        duration?: number,
    ) => any;
    visible: boolean;
    onClose: () => void;
}

const { TimePicker } = DatePicker;

const SetPlaybackModal: React.FC<IProps> = props => {
    const { live, updateLiveState, visible = false } = props;

    const [playBackStart, setPlayBackStart] = useState(0);
    const [playBackEnd, setPlayBackEnd] = useState(0);
    const [title, setTitle] = useState(live.title);
    const [duration, setDuration] = useState(0);
    const [hhmmssObj, setHhmmssObj] = useState(utils.secondsToHHMMSS(duration));
    const [streamId, setStreamId] = useState('');

    // 回放时间不可选时间段
    const disabledTime = (time: number, type: 'hour' | 'minute' | 'second', hhmmssObj: any) => {
        let disable = false;
        // 只判断小时。由于分钟、秒没有好的方法去限制，所以只进行简单判断，如最大是05:10:24，如过此事选的4点，则所有的分钟都可选
        if (type === 'hour' && time > hhmmssObj.hh) {
            disable = true;
        }
        return disable;
    };

    const onConfirm = () => {
        // 合法性校验
        if (playBackStart >= playBackEnd || playBackStart >= duration || playBackEnd > duration) {
            utils.toast('warn', '请正确设置回放时间段');
            return false;
        }
        if (!title) {
            utils.toast('warn', '主题字段不能为空');
            return false;
        }
        // 回放设置时间时，传相对时间
        updateLiveState(
            live.id,
            consts.LIVE_STATE.PLAY_BACK,
            playBackStart,
            playBackEnd,
            title.trim(),
            duration,
        );
        props.onClose();
        return true;
    };

    const getStreamId = async () => {
        const res = await request.get('/api/live/streamState', {
            busId: url.commonParams.providerId,
            busLine: 1,
            liveId: live.id,
        });
        if (res.data) {
            setStreamId(res.data.streamId);
        } else {
            utils.toast('error', res.msg || '');
        }
    };

    useEffect(() => {
        getStreamId();
    }, []);

    useEffect(() => {
        const video: HTMLMediaElement | null = document.querySelector('#play-back-video');
        // 处理视频问题，hls.js只能等video加载完了再处理，所以此处滞后
        setTimeout(() => {
            if (Hls.isSupported() && video) {
                const hls = new Hls();
                const urls = url.getLiveUrl({});
                hls.loadSource(`${urls.playbackStreamUri}${streamId}.m3u8`);

                hls.attachMedia(video);
                hls.on(Hls.Events.MANIFEST_PARSED, () => {
                    video.play();
                });
                hls.on(Hls.Events.ERROR, (event, data) => {
                    console.log('error', event, data);
                    // st环境有时候会误报
                    if (data && data.details === 'bufferSeekOverHole') {
                        return;
                    }
                    utils.toast('error', '加载视频失败，请稍后再试');
                });
                hls.on(Hls.Events.LEVEL_LOADED, (event, data) => {
                    console.log('level loaded', data);
                    const { details } = data;
                    const intDuration = Math.floor(details.totalduration || 0);
                    if (details.live === false) {
                        setDuration(intDuration);
                        setHhmmssObj(utils.secondsToHHMMSS(intDuration));
                        setPlayBackEnd(intDuration);
                    }
                });
            }
        }, 600);
    }, [streamId]);

    return (
        <Modal
            visible={visible}
            onConfirm={onConfirm}
            onCancel={props.onClose}
            backdropClosable={false}
            style={{ width: 500 }}
            title="设置回放"
        >
            <div className="live-window play-back">
                {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                <video
                    id="play-back-video"
                    controls
                    className={live.type === 1 ? 'live-type-vertical' : 'live-type-horizontal'}
                />
                <div className="play-back-item">
                    <span className="select-label">回放时间段：</span>
                    <div className="select-value">
                        <div className="select-time">
                            <TimePicker
                                onChange={(hms: any) => {
                                    setPlayBackStart(utils.hhmmssToSeconds(hms));
                                }}
                                placeholder="回放开始时间"
                                size="small"
                                defaultValue="00:00:00"
                                disabledTime={(time, type) => disabledTime(time, type, hhmmssObj)}
                            />
                        </div>
                        <span className="mgl10 mgr10">至</span>
                        <div className="select-time">
                            <TimePicker
                                onChange={(hms: any) => {
                                    setPlayBackEnd(utils.hhmmssToSeconds(hms));
                                }}
                                placeholder="回放结束时间"
                                size="small"
                                value={utils.secondsToHHMMSS(playBackEnd).hhmmss}
                                disabledTime={(time, type) => disabledTime(time, type, hhmmssObj)}
                                className="select-time"
                            />
                        </div>
                    </div>
                </div>
                <div className="play-back-item">
                    <span className="select-label">直播主题：</span>
                    <div className="select-value">
                        <Input
                            placeholder="请输入直播主题"
                            defaultValue={live.title}
                            style={{ width: 335 }}
                            maxLength={10}
                            onChange={e => {
                                setTitle(e.target.value);
                            }}
                        />
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default SetPlaybackModal;
